import { yupResolver } from "@hookform/resolvers/yup";
import { useDisclosure } from "@nextui-org/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { deleteFetcher, getFetcher, postFetcher } from "../../services/api";
import { isResponseErrorObject } from "../../services/helpers";
import useSWR from "swr";
import { asalMuatValidation } from "../../services/validation/asal-muat.validation";
import useListCompany from "../useListCompany";
import { useSelectedCompany } from "../useSelectedCompany";

export default function useTujuanBongkar() {
    // Filter tabel: otomatis mengikuti perusahaan milik pengguna untuk
    // non-super-admin, null untuk super admin supaya bisa memilih sendiri.
    const [selectedCompany, setSelectedCompany] = useSelectedCompany();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [editId, setEditId] = useState(null);
    const [isEdit, setIsEdit] = useState(false);

    const {isOpen: isOpenModalForm, onOpen: onOpenModalForm, onOpenChange: onOpenChangeModalForm, onClose: onCloseModalForm} = useDisclosure();
    const { register, handleSubmit, reset, control, watch, setValue, formState: { errors, isSubmitting, dirtyFields } } = useForm({resolver: yupResolver(asalMuatValidation)});
    
    const provinceId = watch('provinceId')
    const regencyId = watch('regencyId')
    const districtId = watch('districtId')
    
    const { dataCompany, isLoadingCompany } = useListCompany()
    // Perusahaan milik pengguna yang sedang login; null bila super admin.
    const [perusahaanLogin] = useSelectedCompany();

    /*
     * Daftar pilihan perusahaan tujuan bongkar.
     *
     * Bukan isian bebas: hanya perusahaan yang sudah tertaut SIMPEL dan BUKAN
     * importir. Pengecualiannya ketat - perusahaan yang merangkap importir dan
     * distributor pun tidak muncul - jadi dipakai tipePerusahaanExclude, bukan
     * daftar tipe yang diizinkan. Penyaringan di backend supaya 8.585 baris
     * perusahaan tidak perlu diunduh semuanya.
     */
    // Dua syarat sekaligus, bukan sekadar pengecualian: harus PUNYA salah satu
    // tipe selain impor, lalu yang punya tipe impor dibuang. Tanpa syarat
    // pertama, perusahaan yang kolom tipePerusahaan-nya kosong ikut lolos -
    // 3.886 baris, bukan 2.215.
    const TIPE_DIIZINKAN = ['Produksi B3', 'Distribusi B3', 'Menggunakan B3', 'Mengangkut B3'].join(',')
    const TIPE_DIKECUALIKAN = 'Impor B3'
    const { data: dataTujuanTerdaftar, isLoading: isLoadingTujuanTerdaftar } = useSWR(
        `/api/company/search-company?returnAll=true&hasIdCompanySimpel=true` +
        `&tipePerusahaan=${encodeURIComponent(TIPE_DIIZINKAN)}` +
        `&tipePerusahaanExclude=${encodeURIComponent(TIPE_DIKECUALIKAN)}`,
        getFetcher
    )

    // Yang disimpan adalah nama (teks) dan ada perusahaan yang namanya sama
    // persis - didedup supaya tidak muncul opsi kembar.
    const opsiTujuanTerdaftar = [
        ...new Map(
            (dataTujuanTerdaftar?.data ?? [])
                .filter((item) => item?.name)
                .map((item) => [item.name.trim(), { label: item.name.trim(), value: item.name.trim(), perusahaan: item }])
        ).values(),
    ].sort((a, b) => a.label.localeCompare(b.label, 'id'))
    const { data, isLoading, mutate } = useSWR(selectedCompany ? `/api/company/search-perusahaan-tujuan-bongkar?page=${page + 1}&limit=${pageSize}&companyId=${selectedCompany.value}` : null, getFetcher);
    const { data: dataProvince } = useSWR('/api/location/provinces', getFetcher)
    const { data: dataRegency } = useSWR(provinceId ? `/api/location/cities?provinceId=${provinceId}`: null, getFetcher)
    const { data: dataDistrict } = useSWR(regencyId ? `/api/location/districts?regencyId=${regencyId}`: null, getFetcher)
    const { data: dataVillage } = useSWR(districtId ? `/api/location/villages?districtId=${districtId}`: null, getFetcher)

    function onClickEdit(item) {    
        setEditId(item.id);
        setIsEdit(true);
        reset({
            companyId: item.companyId,
            namaPerusahaan: item.namaPerusahaan,
            alamat: item.alamat,   
            locationType: item.locationType,   
            latitude: item.latitude,   
            longitude: item.longitude,   
            provinceId: item.provinceId,  
            regencyId: item.regencyId,
            districtId: item.districtId,
            villageId: item.villageId  
        });
        onOpenChangeModalForm();
    }

    /*
     * Buka form TAMBAH dengan perusahaan sudah terisi.
     *
     * Disamakan dengan useAsalMuat: tombol Tambah sebelumnya memanggil onOpen
     * useDisclosure langsung sehingga pengguna harus memilih perusahaannya
     * sendiri padahal pilihannya sudah pasti. Super admin tetap dikosongkan.
     */
    function onOpenFormTambah() {
        setEditId(null);
        setIsEdit(false);
        reset({
            companyId: perusahaanLogin?.value ?? '',
            namaPerusahaan: '',
            alamat: '',
            locationType: '',
            latitude: '',
            longitude: '',
            provinceId: '',
            regencyId: '',
            districtId: '',
            villageId: '',
        });
        onOpenModalForm();
    }

    function onCloseForm() {
        setEditId(null);
        setIsEdit(false);
        reset({
            companyId: '',
            namaPerusahaan: '',
            alamat: '',
            locationType: '',
            latitude: '',
            longitude: '',
            provinceId: '',
            regencyId: '',
            districtId: '',
            villageId: '',
        });
        onCloseModalForm()
    }
    
    async function onSubmitDelete(id) {
        try {
            await deleteFetcher('/api/company/tujuan-bongkar', id);
            mutate()
            toast.success('Tujuan bongkar berhasil dihapus!');
        } catch (error) {
            toast.error('Gagal hapus tujuan bongkar!');
        }
    }

    async function onSubmitForm(data) {
        try {
            if (isEdit) {
                await postFetcher(`/api/company/tujuan-bongkar/${editId}`, data);
                mutate()
                toast.success('Tujuan bongkar berhasil diubah!');
            } else {
                await postFetcher('/api/company/tujuan-bongkar', data);
                mutate()
                const company = dataCompany?.data?.find((item) => item.id === data.companyId)
                setSelectedCompany({label: company.name, value: company.id})
                toast.success('Tujuan bongkar berhasil ditambah!');
            }
            onCloseForm();
        } catch (error) {
            isResponseErrorObject(error.response.data.message)
                ? Object.entries(error.response.data.message).forEach(([key, value]) => {
                    toast.error(value);
                })
                : toast.error(error.response.data.message)
        }
    }

    return {
        modalForm: {
            isOpenModalForm,
            // Sengaja bukan onOpen bawaan useDisclosure - lihat onOpenFormTambah.
            onOpenModalForm: onOpenFormTambah,
            onOpenChangeModalForm,
        },
        hookForm: {
            handleSubmit, 
            register,
            reset, 
            control,
            setValue,
            formState: { errors, isSubmitting, dirtyFields }
        },
        fetchData: {
            data,
            isLoading,
            dataCompany,
            isLoadingCompany,
            dataProvince,
            dataRegency,
            dataDistrict,
            dataVillage,
            opsiTujuanTerdaftar,
            isLoadingTujuanTerdaftar
        },
        state: {
            selectedCompany,
            setSelectedCompany,
            page,
            setPage,
            pageSize,
            setPageSize
        },
        isEdit,
        onClickEdit,
        onCloseForm,
        onSubmitDelete,
        onSubmitForm,
    }
}